/**
 * @file Shared dynamic-import retry boundary and asset-error helpers.
 */
import { captureFetchException } from '@@/bits/error_tracker'
import window from '@@/bits/global'

// Production hashed assets (`/(ui/)assets/*.js|css`) or Vite-dev URLs (`/ui-dev/*.{js,ts,tsx,vue,css}`).
const RECOVERABLE_ASSET_PATH =
  /\/(?:(?:ui\/)?assets\/[^?#\s'")]+\.(?:js|css)|ui-dev\/[^?#\s'")]+\.(?:js|ts|tsx|vue|css))/

// Trailing `?|#|$` stops the match from continuing into an unrelated path suffix.
const RECOVERABLE_ASSET_URL = new RegExp(`${RECOVERABLE_ASSET_PATH.source}(\\?|#|$)`)

const RELATIVE_ASSET_URL_IN_MESSAGE = new RegExp(`${RECOVERABLE_ASSET_PATH.source}(?:\\?[^'")\\s]*)?`)

// Two attempts, not one: a 1s pause is short for the flaky-network population this boundary
// exists for. Spaced like the page-level reloadBackoffsMs ladder in _asset_load_recovery.html.erb.
const RETRY_BACKOFFS_MS = [1000, 3000]

function getAssetLoadErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (error != null && typeof error === 'object') {
    const assetError = error as { href?: unknown; message?: unknown; src?: unknown }
    if (typeof assetError.message === 'string') return assetError.message
    if (typeof assetError.href === 'string') return assetError.href
    if (typeof assetError.src === 'string') return assetError.src
  }
  return String(error ?? '')
}

function isRecoverableAssetUrl(url: string | undefined): boolean {
  return typeof url === 'string' && RECOVERABLE_ASSET_URL.test(url)
}

function getAssetUrlFromAssetLoadError(error: unknown): string | undefined {
  if (error != null && typeof error === 'object') {
    const assetError = error as { href?: unknown; src?: unknown }
    if (typeof assetError.href === 'string' && isRecoverableAssetUrl(assetError.href)) return assetError.href
    if (typeof assetError.src === 'string' && isRecoverableAssetUrl(assetError.src)) return assetError.src
  }

  const message = getAssetLoadErrorMessage(error)
  const absoluteUrlMatches = message.match(/https?:\/\/[^\s'")]+/g) ?? []
  const absoluteAssetUrl = absoluteUrlMatches.find(isRecoverableAssetUrl)
  if (absoluteAssetUrl != null) return absoluteAssetUrl

  return message.match(RELATIVE_ASSET_URL_IN_MESSAGE)?.[0]
}

function isDynamicAssetLoadError(error: unknown): boolean {
  const message = getAssetLoadErrorMessage(error)
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Failed to fetch module script') ||
    message.includes('Failed to load module script') ||
    message.includes('Load failed for module') ||
    (getAssetUrlFromAssetLoadError(error) != null && /(fetch|import|module|load)/i.test(message))
  )
}

function cacheBustAssetUrl(url: string): string {
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}padlet-js-retry=${Date.now()}`
}

function importCacheBustedAsset<T>(url: string): Promise<T> {
  return import(/* @vite-ignore */ url) as Promise<T>
}

// Vite compiles the call site's `import('@@/x')` into `import("./x-<hash>.js")` (or
// `import("/ui-dev/x.ts")` in development), so the loader's own source names the module the
// caller asked for. `null` when the source shows no such import (a loader that was inlined or
// wrapped some other way).
function getRequestedModulePath(loader: () => unknown): string | null {
  const source = String(loader)
  const specifier = source.match(/import\(\s*["']([^"']+)["']\s*\)/)?.[1]
  if (specifier == null) return null
  const path = specifier
    .replace(/^(?:\.\.?\/)+/, '')
    .replace(/^\/+/, '')
    .split(/[?#]/)[0]
  return path === '' ? null : path
}

// The failed URL may name a dependency of the requested module rather than the module itself.
// Importing that dependency directly would resolve with the dependency's namespace, not the
// caller's module, so only a URL whose path ends in the requested module's path is retried that way.
function isRequestedModuleUrl(failedUrl: string, requestedModulePath: string | null): boolean {
  if (requestedModulePath == null) return true
  let failedPath: string
  try {
    failedPath = new URL(failedUrl, 'https://padlet.invalid').pathname
  } catch {
    return false
  }
  return failedPath === `/${requestedModulePath}` || failedPath.endsWith(`/${requestedModulePath}`)
}

// A retry imports the chunk URL directly, bypassing the export-name mapping the bundler applies
// at the original import site. When the chunk's names are minified (`export{o as n,c as t}`) the
// namespace is not what the caller destructures, so treat it as a failed retry.
function isUsableNamespace(importedModule: unknown): boolean {
  if (importedModule == null || typeof importedModule !== 'object') return false
  const exportNames = Object.keys(importedModule)
  // A wrapper chunk exports only mangled <=2-char names. A caller-facing namespace has at least one readable name.
  return exportNames.some((exportName) => exportName.length > 2)
}

interface ImportWithRetryOptions {
  // The retry imports the failed chunk's URL directly. When that URL named a
  // dependency of the requested module rather than the module itself, the retry
  // resolves with the wrong namespace; a caller that knows the shape it needs
  // (a Vue component, say) can reject such a result here and it is treated as a
  // failed retry.
  isExpectedModule?: (importedModule: unknown) => boolean
}

function importWithRetry<T>(
  loader: () => Promise<T>,
  retryImport: (url: string) => Promise<T> = importCacheBustedAsset,
  options: ImportWithRetryOptions = {},
): Promise<T> {
  return loader().catch((firstError: unknown) => {
    // Non-asset failures are not retryable; surface them immediately.
    if (!isDynamicAssetLoadError(firstError)) {
      throw firstError
    }

    return new Promise<T>((resolve, reject) => {
      let attemptIndex = 0

      const runAttempt = (): void => {
        const attempt = attemptIndex + 1
        const backoffMs = RETRY_BACKOFFS_MS[attemptIndex]

        setTimeout(() => {
          const failedUrl = getAssetUrlFromAssetLoadError(firstError)
          // A failed import() is stored in the HTML module map, so a second
          // loader() call of the same specifier replays the rejection with no
          // network request. Retry through a unique query string instead. That
          // only yields the caller's module when the failed URL is that module;
          // a failed dependency goes back through loader(), which either
          // recovers the whole graph or fails again into page-level recovery.
          const retryDirectly = failedUrl != null && isRequestedModuleUrl(failedUrl, getRequestedModulePath(loader))
          const retry = retryDirectly ? retryImport(cacheBustAssetUrl(failedUrl)) : loader()
          retry
            .then((importedModule) => {
              if (!isUsableNamespace(importedModule) || options.isExpectedModule?.(importedModule) === false) {
                // The chunk loaded but its namespace is unusable, or it is not the
                // module the caller asked for; give up as if the retry failed.
                // treatAsNetworkFailure because firstError is a native import() rejection,
                // not a @padlet/fetch FetchError — isNetworkFetchError() cannot recognise it,
                // and without the flag the event ships with only an `outcome` tag and is
                // invisible to the network-failure dashboard. Matches the retry_succeeded
                // call below, which reports the same firstError.
                captureFetchException(
                  firstError,
                  {
                    source: 'dynamicImportRetry',
                    outcome: 'retry_export_mismatch',
                  },
                  { treatAsNetworkFailure: true },
                )
                window.PadletAssetLoadRecovery?.recover?.(
                  'dynamic-import-post-mount',
                  getAssetUrlFromAssetLoadError(firstError),
                  getAssetLoadErrorMessage(firstError),
                )
                reject(firstError)
                return
              }

              // Retry recovered the module; report the first failure without surfacing it.
              captureFetchException(
                firstError,
                {
                  source: 'dynamicImportRetry',
                  outcome: 'retry_succeeded',
                  // Bounded to '1' | '2' — safe as a tag, and the only way to see whether the second attempt is earning its round trip.
                  retry_attempt: String(attempt),
                },
                { treatAsNetworkFailure: true },
              )
              resolve(importedModule)
            })
            .catch((retryError: unknown) => {
              attemptIndex += 1
              if (attemptIndex < RETRY_BACKOFFS_MS.length) {
                runAttempt()
                return
              }

              window.PadletAssetLoadRecovery?.recover?.(
                'dynamic-import-post-mount',
                getAssetUrlFromAssetLoadError(retryError),
                getAssetLoadErrorMessage(retryError),
              )
              reject(retryError)
            })
        }, backoffMs)
      }

      runAttempt()
    })
  })
}

export {
  getAssetLoadErrorMessage,
  getAssetUrlFromAssetLoadError,
  importWithRetry,
  isDynamicAssetLoadError,
  isRecoverableAssetUrl,
}
